import React, { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

function getOS() {
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
}

function isInstalled() {
  return window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
}

export default function InstallBanner() {
  const [prompt, setPrompt] = useState(window.__pwaInstallPrompt || null);
  const [visible, setVisible] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const os = getOS();

  useEffect(() => {
    if (isInstalled()) return;

    // Check if snoozed recently (7 days)
    const snoozed = localStorage.getItem("pwa_snoozed");
    if (snoozed && Date.now() - Number(snoozed) < 7 * 24 * 60 * 60 * 1000) return;

    // Also listen for late-arriving prompt
    const handler = (e) => {
      e.preventDefault();
      window.__pwaInstallPrompt = e;
      setPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Show banner after 3s — either native prompt or manual guide
    const t = setTimeout(() => setVisible(true), 3000);
    return () => {
      clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  async function handleInstall() {
    if (prompt) {
      prompt.prompt();
      await prompt.userChoice;
      setVisible(false);
    } else {
      // No native prompt — show manual guide
      setShowGuide(true);
    }
  }

  function snooze() {
    localStorage.setItem("pwa_snoozed", Date.now().toString());
    setVisible(false);
    setShowGuide(false);
  }

  if (isInstalled() || !visible) return null;

  // Manual install guide modal
  if (showGuide) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center p-3">
        <div className="bg-white rounded-2xl w-full max-w-lg p-5 shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <img src="/logo192.png" alt="" className="w-8 h-8 rounded-xl" />
              <p className="font-bold text-gray-800">Install CampusSwap</p>
            </div>
            <button onClick={snooze} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
          </div>

          {os === "ios" ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-3">Follow these steps in Safari:</p>
              {[
                { n: 1, text: 'Tap the Share button (□↑) at the bottom of Safari' },
                { n: 2, text: 'Scroll down and tap "Add to Home Screen"' },
                { n: 3, text: 'Tap "Add" in the top right — done!' },
              ].map(s => (
                <div key={s.n} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{s.n}</span>
                  <p className="text-sm text-gray-700">{s.text}</p>
                </div>
              ))}
            </div>
          ) : os === "android" ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-3">Follow these steps in Chrome:</p>
              {[
                { n: 1, text: 'Tap the 3-dot menu (⋮) at the top right of Chrome' },
                { n: 2, text: 'Tap "Add to Home Screen" or "Install App"' },
                { n: 3, text: 'Tap "Add" — the app icon appears on your home screen!' },
              ].map(s => (
                <div key={s.n} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{s.n}</span>
                  <p className="text-sm text-gray-700">{s.text}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-3">Follow these steps in Chrome:</p>
              {[
                { n: 1, text: 'Click the install icon (⊕) in the address bar on the right side' },
                { n: 2, text: 'Or click the 3-dot menu → "Install CampusSwap..."' },
                { n: 3, text: 'Click "Install" in the popup — done!' },
              ].map(s => (
                <div key={s.n} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{s.n}</span>
                  <p className="text-sm text-gray-700">{s.text}</p>
                </div>
              ))}
            </div>
          )}

          <button onClick={snooze}
            className="mt-5 w-full bg-gray-100 text-gray-600 py-3 rounded-xl text-sm font-semibold">
            Maybe Later
          </button>
        </div>
      </div>
    );
  }

  // Bottom banner
  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 w-full max-w-lg px-3 z-50">
      <div className="bg-gradient-to-r from-blue-700 to-indigo-700 text-white rounded-2xl shadow-2xl p-4 flex items-center gap-3">
        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <Smartphone size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">Install CampusSwap App</p>
          <p className="text-blue-200 text-xs">Get instant alerts & faster access</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={handleInstall}
            className="bg-white text-blue-700 text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1">
            <Download size={13} /> Install
          </button>
          <button onClick={snooze} className="bg-white/20 text-white p-2 rounded-xl">
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
